import { useLogout } from '@/domains/appShell/services/useLogout'
import {
  type AuthConfig,
  Provider,
  msalAtom,
  sessionAtom,
} from '@/domains/appShell/stores/auth.store'
import { isMaintenanceModeAtom } from '@/domains/appShell/stores/maintenance.store'
import { registerProtectedFileClient } from '@/shared/helpers/protectedFile'
import { ApiError, NETWORK_ERROR_KEY, TIMEOUT_ERROR_KEY } from '@/shared/helpers/requestErrors'
import { REST_QUERY_CONFIG } from '@/shared/helpers/restQuery'
import type { ApiConfig, ApiContextType } from '@/shared/types/api'

import { type FC, type PropsWithChildren, createContext, useEffect, useMemo, useRef } from 'react'

import * as Sentry from '@sentry/react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'

type ExtendedAxiosRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean
  skipAuth?: boolean
}

type ExtendedError = AxiosError

/** Query retry policy: only network/timeout errors are retried (max 3 times), never HTTP errors. */
export const shouldRetryQuery = (failureCount: number, error: unknown): boolean => {
  // An HTTP error response is never retried (its `code` is the backend's, not a network one).
  if (error instanceof ApiError) {
    return false
  }
  const typedError = error as ExtendedError
  if (typedError?.response?.status) {
    return false
  }

  // Retry uniquement sur les erreurs réseau/timeout (max 3 fois)
  // - Erreurs de connectivité (ERR_NETWORK, ERR_INTERNET_DISCONNECTED)
  // - Timeouts (ECONNABORTED, ERR_TIMEOUT)
  // - DNS errors (ENOTFOUND)
  const isNetworkError = !!(!typedError.response && typedError.code)
  const isTimeoutError = typedError.code === 'ECONNABORTED' || typedError.code === 'ERR_TIMEOUT'

  return (isNetworkError || isTimeoutError) && failureCount < 3
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: shouldRetryQuery,

      // Délai progressif pour les retry réseau (1s, 2s, 4s)
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),

      staleTime: 60 * 1000,
    },
    mutations: {
      retry: false,
    },
  },
})

export const RestQueryContext = createContext<ApiContextType | null>(null)

/**
 * Provider centralisé pour les requêtes REST avec TanStack Query et Axios.
 *
 * Ce provider combine TanStack Query pour la gestion de cache et Axios pour
 * les requêtes HTTP. Il configure automatiquement l'authentification, gère
 * les erreurs globales et fournit une instance Axios partagée à tous les
 * hooks de requête. Les intercepteurs ajoutent automatiquement les headers
 * d'authentification et gèrent les erreurs 401/500.
 *
 * Fonctionnalités :
 * - Instance Axios configurée avec baseURL et timeout
 * - Intercepteur de requête pour l'authentification automatique
 * - Headers : Authorization (Bearer), AuthorizationProvider, X-Authorization
 * - Intercepteur de réponse pour la gestion d'erreurs globale
 * - Déconnexion automatique sur erreurs 401 et 500
 * - Context React pour partager l'instance entre composants
 * - QueryClient TanStack Query avec configuration par défaut
 * - Support du flag skipAuth pour les requêtes publiques
 *
 * Workflow d'authentification :
 * 1. Chaque requête reçoit automatiquement les tokens de session
 * 2. En cas d'erreur 401, tente un retry puis déconnecte
 * 3. Les erreurs 500 déclenchent une déconnexion immédiate
 * 4. Le flag skipAuth permet de contourner l'authentification
 *
 * @param config - Configuration API (baseURL, timeout)
 * @param auth - Configuration Auth
 * @param children - Composants enfants ayant accès au contexte REST
 * @returns JSX avec providers TanStack Query et contexte API
 */
export const RestQueryProvider: FC<
  PropsWithChildren<{
    config?: ApiConfig
    auth?: AuthConfig
  }>
> = ({
  config = {
    baseURL: 'https://api.example.com',
    timeout: 20000,
  },
  auth,
  children,
}) => {
  const [session, setSession] = useAtom(sessionAtom)
  const msal = useAtomValue(msalAtom)
  const setMaintenanceMode = useSetAtom(isMaintenanceModeAtom)

  const { logout } = useLogout()

  const isRefreshingRef = useRef(false)
  const failedQueueRef = useRef<
    Array<{
      resolve: (_token: string) => void
      reject: (_error: Error) => void
    }>
  >([])

  const processQueue = (error: Error | null, token: string | null = null) => {
    failedQueueRef.current.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error)
      } else {
        resolve(token!)
      }
    })

    failedQueueRef.current = []
  }

  const axiosInstance = useMemo(() => {
    const refreshAccessToken = async () => {
      try {
        if (session?.provider === Provider.MSAL && msal) {
          const accounts = msal.getAllAccounts()

          if (!accounts.length) {
            // TODO use logger
            // eslint-disable-next-line no-console
            console.error('No accounts found')
            return
          }

          const silentRequest = {
            scopes: auth?.entra.scopes || [],
            account: accounts[0],
            forceRefresh: true,
          }

          const response = await msal.acquireTokenSilent(silentRequest)

          setSession(prevSession => {
            if (!prevSession) return null
            return {
              ...prevSession,
              accessToken: response.accessToken,
            }
          })

          return response.accessToken
        }
      } catch (error) {
        // TODO use logger
        // eslint-disable-next-line no-console
        console.error('Token refresh failed:', error)
        return null
      }
    }

    const handle401Error = async (
      error: ExtendedError,
      originalRequest: ExtendedAxiosRequestConfig,
    ) => {
      if (originalRequest._retry) {
        await logout()
        return Promise.reject(
          // @ts-expect-error message is not found on AxiosError type for unknown reason
          error instanceof Error ? error : new Error(error.message ?? 'Authentication failed'),
        )
      }

      if (isRefreshingRef.current) {
        return new Promise((resolve, reject) => {
          failedQueueRef.current.push({ resolve, reject })
        })
          .then(token => {
            originalRequest.headers['Authorization'] = `Bearer ${token}`
            return instance(originalRequest)
          })
          .catch(async err => {
            await logout()
            return Promise.reject(
              err instanceof Error ? err : new Error(err.message ?? 'Queue processing failed'),
            )
          })
      }

      return handleTokenRefresh(error, originalRequest)
    }

    const handleTokenRefresh = async (
      error: ExtendedError,
      originalRequest: ExtendedAxiosRequestConfig,
    ) => {
      originalRequest._retry = true
      isRefreshingRef.current = true

      try {
        const newAccessToken = await refreshAccessToken()

        if (newAccessToken) {
          processQueue(null, newAccessToken)

          originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`

          return instance(originalRequest)
        } else {
          processQueue(error, null)
          await logout()
          return Promise.reject(
            // @ts-expect-error message is not found on AxiosError type for unknown reason
            error instanceof Error ? error : new Error(error.message ?? 'Token refresh failed'),
          )
        }
      } catch (refreshError) {
        processQueue(refreshError as Error, null)
        await logout()
        return Promise.reject(
          refreshError instanceof Error ? refreshError : new Error(String(refreshError)),
        )
      } finally {
        isRefreshingRef.current = false
      }
    }

    const logErrorToSentry = (
      error: ExtendedError,
      originalRequest: ExtendedAxiosRequestConfig,
    ) => {
      if (error.response?.status && error.response.status >= 400) {
        Sentry.captureException(error, {
          tags: {
            api_endpoint: error.config?.url,
            status_code: error.response?.status,
            method: error.config?.method?.toUpperCase(),
            provider: session?.provider,
            retry_attempted: !!originalRequest._retry,
          },
          contexts: {
            request: {
              url: error.config?.url,
              method: error.config?.method,
              data: error.config?.data,
              headers: error.config?.headers,
            },
            response: {
              status: error.response?.status,
              statusText: error.response?.statusText,
              data: error.response?.data,
            },
          },
          level: error.response?.status >= 500 ? 'error' : 'warning',
        })

        if (error.response?.status === 401) {
          Sentry.addBreadcrumb({
            message: 'Authentication failed - triggering logout',
            category: 'auth',
            level: 'warning',
          })
        }
      }
    }

    const instance = axios.create({
      baseURL: config.baseURL,
      timeout: config.timeout,
    })

    instance.interceptors.request.use(async (requestConfig: ExtendedAxiosRequestConfig) => {
      if (!requestConfig.skipAuth && !requestConfig._retry) {
        if (session?.accessToken) {
          requestConfig.headers.set('Authorization', `Bearer ${session.accessToken}`)
          requestConfig.headers.set('AuthorizationProvider', session.provider)
        }
      }
      // Uploads go through this same instance: raise the timeout for multipart bodies,
      // keeping any disabled (0 / undefined) or longer per-request timeout.
      if (requestConfig.data instanceof FormData && requestConfig.timeout) {
        requestConfig.timeout = Math.max(requestConfig.timeout, REST_QUERY_CONFIG.uploadTimeout)
      }
      return requestConfig
    })

    instance.interceptors.response.use(
      response => response,
      // False positive: the interceptor runs per request, never during render — the refs it
      // reaches through `handle401Error` are only read when a response comes back.
      // eslint-disable-next-line react-hooks/refs
      async error => {
        const originalRequest = error.config

        // Aucune réponse reçue : timeout ou perte réseau (pas d'erreur HTTP)
        if (!error.response) {
          const isTimeout = error.code === 'ECONNABORTED' || error.code === 'ERR_TIMEOUT'
          throw new Error(isTimeout ? TIMEOUT_ERROR_KEY : NETWORK_ERROR_KEY)
        }

        if (error.response?.data) {
          if (error.response.data.code) {
            error.response.originalStatus = error.response.status
            error.response.code = error.response.data.code
          }

          if (error.response.data.errors || error.response.data.detail) {
            error.message =
              error.response.data.errors
                ?.map((error: { code: string; message: string }) => error.message)
                .join('\n') || error.response.data.detail
          }
        }

        if (error.response?.status === 401) {
          return handle401Error(error, originalRequest)
        }

        if (error.response?.status === 503) {
          setMaintenanceMode(true)
          Sentry.addBreadcrumb({
            message: 'Maintenance mode detected (503)',
            category: 'maintenance',
            level: 'warning',
          })
          throw new Error('error.maintenance')
        }

        logErrorToSentry(error, originalRequest)

        // Same message as a plain `Error`, plus the HTTP status for the callers' boundaries.
        throw new ApiError(error.message, error.response.status, error.response.data?.code)
      },
    )

    return instance
  }, [
    config.baseURL,
    config.timeout,
    session?.provider,
    session?.accessToken,
    msal,
    auth?.entra.scopes,
    setSession,
    setMaintenanceMode,
    logout,
  ])

  useEffect(() => {
    registerProtectedFileClient(axiosInstance)
  }, [axiosInstance])

  return (
    <RestQueryContext.Provider value={{ config, axiosInstance }}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </RestQueryContext.Provider>
  )
}
